import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Autocomplete,
  Paper,
  Stack,
  InputAdornment,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const IVA_OPTIONS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 15, label: "15%" },
  { value: 999, label: "No objeto de IVA" },
];

const norm = (t) =>
  (t || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const EditProduct = ({ product, onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [barcode, setBarcode] = useState("");
  const [codigoAux, setCodigoAux] = useState("");
  const [seriado, setSeriado] = useState(false);
  const [pvp1, setPvp1] = useState("");
  const [ivaPorc, setIvaPorc] = useState(15);
  const [stockMin, setStockMin] = useState("");
  const [estado, setEstado] = useState(true);
  const [ecommerce, setEcommerce] = useState(true);
  const [description, setDescription] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [sections, setSections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const [sectionId, setSectionId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");

  const [sectionNameSeed, setSectionNameSeed] = useState("");
  const [categoryNameSeed, setCategoryNameSeed] = useState("");
  const [brandNameSeed, setBrandNameSeed] = useState("");

  const [productCodeMode, setProductCodeMode] = useState("manual");
  const [autoGenerateCode, setAutoGenerateCode] = useState(false);

  const fetchProductCodeMode = async () => {
    const r = await fetch("/productos/modo-codigo/", { credentials: "include" });
    const d = await r.json();
    if (r.ok) {
      const mode = d?.mode === "automatico" ? "automatico" : "manual";
      setProductCodeMode(mode);
      setAutoGenerateCode(mode === "automatico");
    }
  };

  const fetchSections = async () => {
    const r = await fetch("/categorias/secciones/", { credentials: "include" });
    const d = await r.json();
    if (r.ok) {
      const arr = Array.isArray(d) ? d : [];
      arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "es", { sensitivity: "base" }));
      setSections(arr);
    }
  };

  const fetchBrands = async () => {
    const r = await fetch("/categorias/marcas/", { credentials: "include" });
    const d = await r.json();
    if (r.ok) {
      const arr = Array.isArray(d) ? d : [];
      arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "es", { sensitivity: "base" }));
      setBrands(arr);
    }
  };

  const fetchCategoriesBySection = async (sid) => {
    if (!sid) {
      setCategories([]);
      return;
    }
    const r = await fetch(`/categorias/seccion/${sid}/`, { credentials: "include" });
    const d = await r.json();
    if (r.ok) {
      const arr = Array.isArray(d) ? d : [];
      arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "es", { sensitivity: "base" }));
      setCategories(arr);
    }
  };

  useEffect(() => {
    fetchProductCodeMode();
    fetchSections();
    fetchBrands();
  }, []);

  useEffect(() => {
    if (!product) return;

    setName(product.name || "");
    setCode(product.code || "");
    setBarcode(product.barcode || "");
    setCodigoAux(product.codigo_auxiliar || "");
    setSeriado(!!product.seriado);
    setPvp1(String(product.pvp1 ?? ""));
    setIvaPorc(product.iva_porcentaje ?? 15);
    setStockMin(product.stock_minimo ?? "");
    setEstado(product.estado === "activo");
    setEcommerce(product.ecommerce !== undefined ? product.ecommerce : true);
    setDescription(product.description || "");
    setExistingImages(product.images || []);
    setNewImages([]);

    const rawSectionId = product.section_id ?? product.section ?? "";
    const rawCategoryId = product.category_id ?? product.category ?? "";
    const rawBrandId = product.brand_id ?? product.brand ?? "";

    const sIdIsNumeric = rawSectionId !== "" && !Number.isNaN(Number(rawSectionId));
    const cIdIsNumeric = rawCategoryId !== "" && !Number.isNaN(Number(rawCategoryId));
    const bIdIsNumeric = rawBrandId !== "" && !Number.isNaN(Number(rawBrandId));

    setSectionId(sIdIsNumeric ? String(rawSectionId) : "");
    setCategoryId(cIdIsNumeric ? String(rawCategoryId) : "");
    setBrandId(bIdIsNumeric ? String(rawBrandId) : "");

    setSectionNameSeed(!sIdIsNumeric ? String(rawSectionId || "") : "");
    setCategoryNameSeed(!cIdIsNumeric ? String(rawCategoryId || "") : "");
    setBrandNameSeed(!bIdIsNumeric ? String(rawBrandId || "") : "");

    if (sIdIsNumeric) fetchCategoriesBySection(String(rawSectionId));
  }, [product]);

  useEffect(() => {
    if (!sectionId) return;
    fetchCategoriesBySection(sectionId);
  }, [sectionId]);

  useEffect(() => {
    if (!sectionId || !sectionNameSeed || sections.length === 0) return;
    if (sectionId) return;
    const found = sections.find((s) => norm(s.name) === norm(sectionNameSeed));
    if (found) setSectionId(String(found.id));
  }, [sections, sectionNameSeed, sectionId]);

  useEffect(() => {
    if (!brandNameSeed || brands.length === 0) return;
    if (brandId) return;
    const found = brands.find((b) => norm(b.name) === norm(brandNameSeed));
    if (found) setBrandId(String(found.id));
  }, [brands, brandNameSeed, brandId]);

  useEffect(() => {
    if (!categoryNameSeed || categories.length === 0) return;
    if (categoryId) return;
    const found = categories.find((c) => norm(c.name) === norm(categoryNameSeed));
    if (found) setCategoryId(String(found.id));
  }, [categories, categoryNameSeed, categoryId]);

  useEffect(() => {
    if (!sectionId || categories.length === 0) return;
    if (!categoryId) return;
    const ok = categories.some((c) => String(c.id) === String(categoryId));
    if (!ok) setCategoryId("");
  }, [categories, sectionId, categoryId]);

  useEffect(() => {
    async function updateCodeIfAutomatic() {
      if (!autoGenerateCode) return;
      if (!sectionId || !categoryId || !brandId) return;

      if (!product) return;

      const structureChanged =
        String(product.section_id ?? product.section) !== String(sectionId) ||
        String(product.category_id ?? product.category) !== String(categoryId) ||
        String(product.brand_id ?? product.brand) !== String(brandId);

      if (!structureChanged) return;

      const selectedSection = sections.find(s => String(s.id) === String(sectionId));
      const selectedCategory = categories.find(c => String(c.id) === String(categoryId));
      const selectedBrand = brands.find(b => String(b.id) === String(brandId));

      if (!selectedSection || !selectedCategory || !selectedBrand) return;

      const base =
        selectedSection.sequential_code +
        selectedCategory.sequential_code +
        selectedBrand.sequential_code;

      const r = await fetch(`/productos/next/?base=${base}`, { credentials: "include" });
      const d = await r.json();

      if (r.ok && d?.next_code) {
        setCode(d.next_code);
      }
    }

    updateCodeIfAutomatic();
  }, [autoGenerateCode, sectionId, categoryId, brandId]);

  const ivaValor = useMemo(() => {
    const base = parseFloat(pvp1 || 0);
    if (ivaPorc === 999) return "0.00";
    return (base * (ivaPorc / 100)).toFixed(2);
  }, [pvp1, ivaPorc]);

  const precioFinal = useMemo(() => {
    const base = parseFloat(pvp1 || 0);
    const iva = parseFloat(ivaValor || 0);
    return (base + iva).toFixed(2);
  }, [pvp1, ivaValor]);

  const removeExistingImage = (img) => {
    setExistingImages((prev) => prev.filter((i) => i !== img));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setNewImages((prev) => [...prev, ...files]);
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const payload = {
      name,
      code,
      barcode,
      codigo_auxiliar: codigoAux,
      seriado,
      section: sectionId,
      category: categoryId,
      brand: brandId,
      pvp1,
      iva_porcentaje: ivaPorc,
      stock_minimo: stockMin,
      estado: estado ? "activo" : "inactivo",
      ecommerce,
      description,
      existingImages,
      newImages,
      auto_generate_code: autoGenerateCode,
      product_code_mode: productCodeMode,
    };
    onSubmit(payload);
  };

  const selectedSection = useMemo(
    () => sections.find((s) => String(s.id) === String(sectionId)) || null,
    [sections, sectionId]
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(categoryId)) || null,
    [categories, categoryId]
  );

  const selectedBrand = useMemo(
    () => brands.find((b) => String(b.id) === String(brandId)) || null,
    [brands, brandId]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
          Editar Producto
        </Typography>      
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Código"
              value={code}
              onChange={(e) => {
                if (!autoGenerateCode) {
                  setCode(e.target.value);
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: autoGenerateCode ? "rgba(21, 101, 192, 0.08)" : "rgba(46, 125, 50, 0.08)",
                  transition: "all .2s ease",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Código de barras"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Código auxiliar"
              value={codigoAux}
              onChange={(e) => setCodigoAux(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={seriado} onChange={(e) => setSeriado(e.target.checked)} />}
              label="Seriado"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={sections}
              value={selectedSection}
              onChange={(_, v) => {
                const id = v ? String(v.id) : "";
                setSectionId(id);
                setCategoryId("");
                setCategoryNameSeed("");
              }}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
              renderInput={(params) => <TextField {...params} fullWidth label="Sección" />}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={categories}
              value={selectedCategory}
              onChange={(_, v) => setCategoryId(v ? String(v.id) : "")}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
              renderInput={(params) => <TextField {...params} fullWidth label="Categoría" />}
              disabled={!sectionId}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={brands}
              value={selectedBrand}
              onChange={(_, v) => setBrandId(v ? String(v.id) : "")}
              getOptionLabel={(opt) => opt?.name || ""}
              isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
              renderInput={(params) => <TextField {...params} fullWidth label="Marca" />}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="PVP1" value={pvp1} onChange={(e) => setPvp1(e.target.value)} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="IVA %"
              value={ivaPorc}
              onChange={(e) => setIvaPorc(parseInt(e.target.value, 10))}
            >
              {IVA_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="IVA $" value={ivaValor} InputProps={{ readOnly: true }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Precio Final" value={precioFinal} InputProps={{ readOnly: true }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Stock mínimo"
              value={stockMin}
              onChange={(e) => setStockMin(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={estado} onChange={(e) => setEstado(e.target.checked)} />}
              label="Activo"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={ecommerce} onChange={(e) => setEcommerce(e.target.checked)} />}
              label={ecommerce ? "Visible en e-commerce" : "No visible en e-commerce"}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={8}
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Imágenes actuales
            </Typography>
            <Grid container spacing={1}>
              {existingImages.map((img, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#fff",
                    }}
                  >
                    <img
                      src={img}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeExistingImage(img)}
                      sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(255,255,255,0.9)" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Nuevas imágenes
            </Typography>
            <Grid container spacing={1}>
              {newImages.map((file, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#fff",
                    }}
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeNewImage(i)}
                      sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(255,255,255,0.9)" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddPhotoAlternateIcon />}
              component="label"
              sx={{ mb: 1, py: 1.2, borderRadius: 2 }}
            >
              Agregar Imágenes
              <input type="file" hidden accept="image/*" multiple onChange={handleImageUpload} />
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!sectionId || !categoryId || !brandId}>
            Guardar Cambios
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default EditProduct;